<template>
  <div class="policy-list-wrap">
    <!-- 保单表格 -->
    <div class="table-wrapper" v-if="policyList.length > 0">
      <table class="policy-table">
        <thead>
          <tr>
            <th>保单号</th>
            <th>保险名称</th>
            <th>投保单位</th>
            <th>状态</th>
            <th>投保日期</th>
            <th>保障期限</th>
            <th>被保人数</th>
            <th>保费（元）</th>
            <th class="col-action">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="policy in policyList" :key="policy.id">
            <td>{{ policy.insurerPolicyCode }}</td>
            <td>{{ policy.productName }}</td>
            <td>{{ policy.name }}</td>
            <td>
              <span class="status-tag" :class="getStatusClass(policy.coverStatus)">
                {{ policy.coverStatusStr }}
              </span>
            </td>
            <td>{{ policy.createTime }}</td>
            <td>{{ policy.effectiveDate }} 至 {{ policy.expiryDate }}</td>
            <td>{{ policy.insuredCount }}人</td>
            <td>{{ policy.premium }}</td>
            <td class="col-action">
              <a
                v-if="policy.coverStatus !== 8"
                class="row-action"
                :class="{ 'is-loading': downloadingId === policy.id }"
                @click.prevent="downloadPolicy(policy)"
              >下载保单</a>
              <a
                v-if="policy.coverStatus !== 9 && policy.coverStatus !== 8"
                class="row-action"
                @click.prevent="applyRefund(policy)"
              >申请退保</a>
              <a
                class="row-action"
                @click.prevent="viewPersonList(policy)"
              >人员清单</a>
              <a
                v-if="policy.coverStatus == 8"
                class="row-action"
                @click.prevent="viewRefundRecord(policy)"
              >退保记录</a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 空状态 -->
    <div class="empty-state" v-else>
      <v-icon size="64" color="#ddd">description</v-icon>
      <p class="empty-text">暂无保单记录</p>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="policyList.length > 0">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        color="#dd122a"
        @input="handlePageChange"
      ></v-pagination>
    </div>

    <!-- 申请退保弹窗 -->
    <v-dialog v-model="refundDialog.visible" max-width="400">
      <v-card>
        <v-card-title class="dialog-title">申请退保</v-card-title>
        <v-card-text class="dialog-content">
          <p>尊敬的客户，本保单已生效，如需办理退保业务，请直接联系保险公司工作人员进行申请。</p>
          <p class="contact-info">联系人：张老师</p>
          <p class="contact-info">联系电话：022-58817632</p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn color="#dd122a" dark @click="refundDialog.visible = false">我知道了</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 退保二次确认弹窗 -->
    <v-dialog v-model="confirmRefundDialog.visible" max-width="400">
      <v-card>
        <v-card-title class="dialog-title">确认退保</v-card-title>
        <v-card-text class="dialog-content">
          <p>您确定要为此保单申请退保吗？</p>
          <p class="policy-info">保单号：{{ confirmRefundDialog.policyNo }}</p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn @click="confirmRefundDialog.visible = false" :disabled="confirmRefundDialog.loading">取消</v-btn>
          <v-btn color="#dd122a" dark @click="confirmRefund" :loading="confirmRefundDialog.loading">确认退保</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 查看人员清单弹窗 -->
    <PersonListDialog ref="personListDialog"/>

    <!-- 退保记录弹窗 -->
    <RefundRecordDialog ref="refundRecordDialog"/>
  </div>
</template>

<script>
import PersonListDialog from './personListDialog.vue';
import RefundRecordDialog from './refundRecordDialog.vue';
import { getPolicyList, getElectronicPolicy, standardCancel } from '@/api/travelInsurance';

export default {
  name: 'PolicyList',
  components: {
    PersonListDialog,
    RefundRecordDialog
  },
  data() {
    return {
      // 保单列表
      policyList: [],
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      totalCount: 0,
      downloadingId: null,

      // 申请退保弹窗
      refundDialog: {
        visible: false,
        policyNo: '',
        policyId: null
      },

      // 退保二次确认弹窗
      confirmRefundDialog: {
        visible: false,
        policyNo: '',
        policyId: null,
        loading: false
      },
    };
  },
  mounted() {
    this.loadPolicyList();
  },
  methods: {
    // 加载保单列表
    async loadPolicyList() {
      try {
        const params = {
          pageIndex: this.currentPage,
          pageSize: this.pageSize
        };
        const res = await getPolicyList(params);
        if (res.code === 0 || res.success) {
          const pageData = res.data || {};
          this.policyList = pageData.records || [];
          this.totalCount = pageData.total || 0;
          this.totalPages = pageData.pages || 1;
        } else {
          this.$message.error(res.msg || '获取保单列表失败');
          this.policyList = [];
        }
      } catch (error) {
        console.error('获取保单列表失败:', error);
        this.$message.error('获取保单列表失败');
        this.policyList = [];
      }
    },

    // 获取状态样式类
    getStatusClass(status) {
      const statusMap = {
        0: 'status-pending',
        5: 'status-active',
        9: 'status-expired',
        8: 'status-canceled'
      };
      return statusMap[status] || '';
    },

    // 分页变化
    handlePageChange(page) {
      this.currentPage = page;
      this.loadPolicyList();
    },

    // 下载电子保单
    async downloadPolicy(policy) {
      this.downloadingId = policy.id;
      try {
        const res = await getElectronicPolicy(policy.id);
        if (res.code === 0 || res.success) {
          if (res.data) {
            const link = document.createElement('a');
            link.href = res.data;
            link.download = `保单_${policy.policyNo}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } else {
          this.$message.error(res.msg || '下载失败');
        }
      } catch (error) {
        console.error('下载电子保单失败:', error);
        this.$message.error('下载电子保单失败');
      } finally {
        this.downloadingId = null;
      }
    },

    // 申请退保
    applyRefund(policy) {
      if (policy.coverStatus === 0) {
        this.confirmRefundDialog.policyNo = policy.insurerPolicyCode;
        this.confirmRefundDialog.policyId = policy.id;
        this.confirmRefundDialog.visible = true;
      } else {
        this.refundDialog.policyNo = policy.policyNo;
        this.refundDialog.policyId = policy.id;
        this.refundDialog.visible = true;
      }
    },

    // 确认退保
    async confirmRefund() {
      if (this.confirmRefundDialog.loading) return;

      this.confirmRefundDialog.loading = true;
      try {
        const res = await standardCancel(this.confirmRefundDialog.policyId);
        if (res.code === 0 || res.success) {
          this.$message.success('退保申请成功');
          this.confirmRefundDialog.visible = false;
          this.loadPolicyList();
        } else {
          this.$message.error(res.msg || '退保申请失败');
        }
      } catch (error) {
        console.error('退保申请失败:', error);
        this.$message.error('退保申请失败');
      } finally {
        this.confirmRefundDialog.loading = false;
      }
    },

    // 查看人员清单
    viewPersonList(policy) {
      this.$refs["personListDialog"].loadList(policy);
    },

    // 搜索人员清单（占位）
    searchPersonList(searchKey) {
      console.log('搜索：', searchKey);
    },

    // 导出人员清单（占位）
    exportPersonList() {
      console.log('导出人员清单');
    },

    // 查看退保记录
    viewRefundRecord(policy) {
      this.$refs["refundRecordDialog"].onLoad(policy);
    },
  }
};
</script>

<style lang="scss" scoped>
.policy-list-wrap {
  width: 100%;
}

// 表格容器
.table-wrapper {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow-x: auto;
  margin-bottom: 20px;
}

// 保单表格
.policy-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  thead {
    background-color: #F5F5F5;

    th {
      padding: 14px 12px;
      text-align: left;
      font-weight: 500;
      color: #333;
      white-space: nowrap;
      border-bottom: 1px solid #E8E8E8;

      &.col-action {
        position: sticky;
        right: 0;
        z-index: 3;
        background-color: #F5F5F5;
        text-align: center;
        box-shadow: -4px 0 6px -4px rgba(0, 0, 0, 0.08);
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #F0F0F0;
      transition: background-color 0.2s;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: #FAFAFA;

        td.col-action {
          background-color: #FAFAFA;
        }
      }
    }

    td {
      padding: 14px 12px;
      color: #262626;
      white-space: nowrap;

      &.col-action {
        position: sticky;
        right: 0;
        z-index: 1;
        background-color: #fff;
        text-align: center;
        box-shadow: -4px 0 6px -4px rgba(0, 0, 0, 0.08);
      }
    }
  }
}

// 状态徽标
.status-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;

  &.status-pending {
    background-color: #DBEAFE;
    color: #007DFF;
  }

  &.status-active {
    background-color: #DCFCE8;
    color: #18813E;
  }

  &.status-expired {
    background-color: #FDF1F2;
    color: #DD122A;
  }

  &.status-canceled {
    background-color: #EEEEEE;
    color: #93969B;
  }
}

// 行内操作文字链接
.row-action {
  display: inline-block;
  margin: 0 8px;
  color: #007DFF;
  cursor: pointer;
  font-size: 13px;
  text-decoration: none;
  transition: color 0.15s;

  &:first-child {
    margin-left: 0;
  }

  &:hover {
    color: #005BB5;
    text-decoration: underline;
  }

  &.is-loading {
    color: #BFBFBF;
    cursor: not-allowed;
    pointer-events: none;
  }
}

// 空状态
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background-color: #fff;
  border-radius: 8px;

  .empty-text {
    margin-top: 16px;
    font-size: 14px;
    color: #999;
  }
}

// 分页
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding: 20px 0;
}

// 弹窗样式
.dialog-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.dialog-content {
  padding: 20px;

  p {
    margin-bottom: 8px;
    font-size: 14px;
    color: #666;

    &.policy-info {
      color: #333;
      font-weight: 500;
    }

    &.contact-info {
      color: #333;
      font-weight: 500;
      margin-bottom: 4px;
    }
  }
}

.dialog-actions {
  padding: 12px 20px;
  justify-content: flex-end;
}
</style>